import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MapPin, Calendar, Clock, Shield, Check, ArrowLeft, Loader2 } from 'lucide-react';
import IMCPanel from './IMCPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '573001234567';

const cityIcons = {
  bogota: '🏛️',
  medellin: '🌸',
  cali: '☀️',
};

export default function BookingStep({ answers, onSuccess }) {
  const [phase, setPhase] = useState('city'); // city | date | contact
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState(null);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [bookedCounts, setBookedCounts] = useState({});
  const [maxPerSlot, setMaxPerSlot] = useState(4);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [time, setTime] = useState('');
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/cities`).then(r => setCities(r.data)).catch(() => {});
  }, []);

  // date constraints: today minimum, 60 days max
  const dateBounds = useMemo(() => {
    const today = new Date();
    const max = new Date();
    max.setDate(max.getDate() + 60);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { min: fmt(today), max: fmt(max) };
  }, []);

  useEffect(() => {
    if (phase !== 'date' || !cityId || !date) return;
    setSlotsLoading(true);
    setSlotsError('');
    setTime('');
    axios.get(`${API}/calendar/slots`, { params: { city: cityId, date } })
      .then(r => {
        if (r.data.closed) setSlotsError(r.data.reason || 'Sin horarios disponibles');
        setSlots(r.data.slots || []);
        setBookedCounts(r.data.booked_counts || {});
        setMaxPerSlot(r.data.max_per_slot || 4);
      })
      .catch((e) => {
        setSlotsError(e?.response?.data?.detail || 'Error al consultar horarios');
        setSlots([]);
      })
      .finally(() => setSlotsLoading(false));
  }, [cityId, date, phase]);

  const cityObj = cities.find(c => c.id === cityId);

  const fullPhone = `+57${contact.phone.replace(/\D/g, '')}`;
  const canSubmit = contact.name.trim().length > 1 && contact.phone.replace(/\D/g, '').length >= 7;

  const submit = async () => {
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await axios.post(`${API}/appointments`, {
        name: contact.name,
        phone: fullPhone,
        email: contact.email,
        city: cityId,
        date,
        time,
        answers,
      });

      // Meta Pixel — Lead event with Advanced Matching
      try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          const nameParts = contact.name.trim().split(/\s+/);
          const firstName = (nameParts[0] || '').toLowerCase();
          const lastName = nameParts.slice(1).join(' ').toLowerCase();
          const emailNorm = (contact.email || '').trim().toLowerCase();
          const phoneNorm = fullPhone.replace(/\D/g, ''); // E.164 digits only

          // Re-init pixel with PII so Meta hashes it client-side (Advanced Matching)
          window.fbq('init', '2276187529981444', {
            em: emailNorm,
            ph: phoneNorm,
            fn: firstName,
            ln: lastName,
            country: 'co',
          });
          // Fire Lead event with contextual info
          window.fbq('track', 'Lead', {
            content_name: 'Valoración Mistika',
            content_category: cityObj?.name || cityId,
            value: 0,
            currency: 'COP',
            appointment_date: date,
            appointment_time: time,
          });
          // Also fire CompleteRegistration for richer attribution
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'Booking confirmed',
            status: true,
          });
        }
      } catch (e) {
        // do not block flow if pixel fails
        // eslint-disable-next-line no-console
        console.warn('Meta Pixel tracking failed', e);
      }

      onSuccess(res.data);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || 'No pudimos agendar la cita. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* IMC + Projection (always visible at the top of step 11) */}
      <IMCPanel answers={answers} />

      {/* Intro message above booking */}
      <div className="quiz-dark p-5 sm:p-6 mb-6 text-center" data-testid="booking-intro">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 mb-3">
          <Check className="w-3.5 h-3.5 text-fuchsia-300" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-fuchsia-200 font-bold">Agenda Tu Cita</span>
        </div>
        <p className="font-display text-lg sm:text-xl text-white leading-snug">
          Agenda tu <span className="italic text-fuchsia-300">Valoración Gratuita</span>.<br className="hidden sm:block" />
          Selecciona ciudad, fecha y horario disponible.<br className="hidden sm:block" /> Tu cita se registrará al instante.
        </p>
      </div>

      {/* Mini header with steps */}
      <div className="flex items-center gap-2 mb-5">
        <StepDot active={phase === 'city'} done={phase !== 'city'} icon={<MapPin className="w-4 h-4" />} label="Ciudad" />
        <Divider />
        <StepDot active={phase === 'date'} done={phase === 'contact'} icon={<Calendar className="w-4 h-4" />} label="Fecha y hora" />
        <Divider />
        <StepDot active={phase === 'contact'} done={false} icon={<Check className="w-4 h-4" />} label="Datos" />
      </div>

      {/* PHASE: CITY */}
      {phase === 'city' && (
        <div className="space-y-3" data-testid="phase-city">
          <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-1">Elige tu ciudad</h3>
          <p className="text-purple-200/70 text-sm mb-4">Selecciona la sede donde deseas tu valoración gratuita.</p>
          {cities.map((c) => (
            <button
              key={c.id}
              onClick={() => { setCityId(c.id); setPhase('date'); }}
              className={`quiz-option-dark w-full text-left flex items-start gap-4 ${cityId === c.id ? 'selected' : ''}`}
              data-testid={`city-${c.id}`}
            >
              <span className="text-3xl mt-0.5">{cityIcons[c.id] || '📍'}</span>
              <span className="flex-1">
                <span className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-bold text-white text-base">{c.name}</span>
                  {c.sede && <span className="text-fuchsia-300 text-xs font-semibold tracking-wider uppercase">· Sede {c.sede}</span>}
                </span>
                <span className="block text-purple-100/80 text-xs mt-0.5">{c.address}</span>
                {c.reference && <span className="block text-purple-300/55 text-[11px] mt-1 italic">{c.reference}</span>}
              </span>
              <span className="text-fuchsia-300 text-lg self-center">→</span>
            </button>
          ))}
        </div>
      )}

      {/* PHASE: DATE & SLOT */}
      {phase === 'date' && (
        <div data-testid="phase-date">
          <button onClick={() => setPhase('city')} className="text-purple-300/80 hover:text-white text-xs font-semibold flex items-center gap-1 mb-3" data-testid="back-to-city">
            <ArrowLeft className="w-3 h-3" /> Cambiar ciudad
          </button>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-1">
            <span className="text-fuchsia-300">{cityObj?.name}</span> · Escoge fecha y hora
          </h3>
          <p className="text-purple-200/70 text-sm mb-4">{cityObj?.address}</p>

          <div className="mb-5">
            <label className="block text-[11px] tracking-[0.25em] uppercase text-purple-300/80 font-bold mb-2">Fecha de la cita</label>
            <input
              type="date"
              value={date}
              min={dateBounds.min}
              max={dateBounds.max}
              onChange={(e) => setDate(e.target.value)}
              className="purple-input-dark"
              data-testid="date-input"
            />
            <p className="text-purple-300/60 text-xs mt-2">
              Lun–Vie: 8:00am – 6:30pm · Sáb: 8:00am – 3:30pm · Dom/festivos: cerrado
            </p>
          </div>

          {date && (
            <div className="mb-5">
              <label className="block text-[11px] tracking-[0.25em] uppercase text-purple-300/80 font-bold mb-2">Horarios disponibles</label>
              {slotsLoading ? (
                <div className="flex items-center gap-2 text-purple-300/70 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Consultando agenda…
                </div>
              ) : slotsError ? (
                <div className="urg-box-dark"><p>{slotsError}</p></div>
              ) : slots.length === 0 ? (
                <div className="urg-box-dark"><p>No hay horarios libres en esta fecha. Prueba otro día.</p></div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slots-grid">
                  {slots.map((s) => (
                    <button
                      key={s}
                      onClick={() => setTime(s)}
                      className={`py-2.5 px-2 rounded-xl border text-sm font-semibold transition-all ${time === s
                        ? 'bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white border-fuchsia-400 shadow-lg shadow-fuchsia-500/40 scale-105'
                        : 'bg-purple-500/10 text-purple-100 border-purple-400/30 hover:border-fuchsia-400 hover:bg-purple-500/20'
                        }`}
                      data-testid={`slot-${s}`}
                    >
                      {fmt12(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            disabled={!cityId || !date || !time}
            onClick={() => setPhase('contact')}
            className="cta-glow w-full"
            data-testid="confirm-datetime-button"
          >
            Agendar Mi Valoración Gratuita →
          </button>
        </div>
      )}

      {/* PHASE: CONTACT */}
      {phase === 'contact' && (
        <div data-testid="phase-contact">
          <button onClick={() => setPhase('date')} className="text-purple-300/80 hover:text-white text-xs font-semibold flex items-center gap-1 mb-3" data-testid="back-to-date">
            <ArrowLeft className="w-3 h-3" /> Cambiar fecha/hora
          </button>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-1">Tus datos para agendar</h3>
          <p className="text-purple-200/70 text-sm mb-4">Te confirmaremos por WhatsApp y enviaremos la invitación al calendario.</p>

          <div className="urg-box-dark mb-4">
            <p className="font-bold text-white mb-1">Resumen de tu cita</p>
            <div className="text-xs leading-relaxed">
              <p>📍 <strong>{cityObj?.name}{cityObj?.sede ? ` · Sede ${cityObj.sede}` : ''}</strong></p>
              <p className="opacity-80 ml-4">{cityObj?.address}</p>
              <p>📅 <strong>{prettyDate(date)}</strong> · <strong>{fmt12(time)}</strong></p>
              <p>⏱ Duración: 45 minutos · 100% gratuita</p>
            </div>
          </div>

          <div className="space-y-3">
            <input className="purple-input-dark" placeholder="Tu nombre completo" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name-input" />
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-100 font-semibold text-sm tracking-wide pointer-events-none select-none flex items-center gap-1">
                🇨🇴 +57
                <span className="text-purple-400/50 ml-1">|</span>
              </span>
              <input
                className="purple-input-dark !pl-[88px]"
                type="tel"
                inputMode="numeric"
                placeholder="311 234 5678"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value.replace(/[^\d\s]/g, '') })}
                data-testid="contact-phone-input"
              />
            </div>
            <input className="purple-input-dark" type="email" placeholder="Correo (para invitación al calendario)" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email-input" />
          </div>

          <div className="flex items-start gap-2 text-purple-300/65 text-xs mt-3">
            <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>Tu información está protegida. Solo se usa para confirmar tu cita gratuita.</p>
          </div>

          {errorMsg && (
            <div className="mt-3 p-3 rounded-xl bg-red-500/15 border border-red-400/40 text-red-200 text-sm">{errorMsg}</div>
          )}

          <button
            disabled={!canSubmit || submitting}
            onClick={submit}
            className="cta-glow w-full mt-5"
            data-testid="submit-booking-button"
          >
            {submitting ? 'Agendando…' : 'Agendar →'}
          </button>
          <p className="text-center text-purple-300/55 text-xs mt-3">Sin compromiso · 100% confidencial</p>
        </div>
      )}
    </div>
  );
}

function StepDot({ active, done, icon, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${done ? 'bg-gradient-to-br from-purple-500 to-fuchsia-500 border-fuchsia-400 text-white' : active ? 'border-fuchsia-400 text-fuchsia-300 bg-purple-500/20' : 'border-purple-700/50 text-purple-400/50'}`}>
        {done ? <Check className="w-3.5 h-3.5" /> : icon}
      </div>
      <span className={`text-[10px] uppercase tracking-wider font-bold hidden sm:inline ${active || done ? 'text-white' : 'text-purple-400/50'}`}>{label}</span>
    </div>
  );
}
function Divider() {
  return <div className="flex-1 h-px bg-purple-700/40" />;
}

function fmt12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const am = h < 12;
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
}
function prettyDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
